import React from 'react';
import { Document, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import LogoPNG from '../../assets/logos/LE Logo - small - resized 2.png';
import EquilibriumLogoPNG from '../../assets/logos/ee-logo-150-pixels.png';
import { resolveQuotationSignatoryCompany } from '@/constants/quotationSignatories';
import {
  DEFAULT_QUOTATION_EXCLUSION_OPTIONS,
  dedupeQuotationExclusions,
} from '@/constants/quotationExclusions';
import {
  DEFAULT_QUOTATION_HOURLY_RATE,
  DEFAULT_QUOTATION_INSPECTION_FEE,
} from '@/constants/quotationDefaults';
import {
  getQuotationScopeCatalogueSelection,
  type QuotationScopeCatalogueSection,
} from '@/constants/quotationOptions';
import {
  QUOTATION_PDF_COLORS as COLORS,
  QUOTATION_PDF_FONT_FAMILY,
  QUOTATION_PDF_FONT_SIZES,
  QUOTATION_PDF_LINE_HEIGHTS,
  QUOTATION_PDF_SPACING,
} from './quotationPdfTheme';

export interface QuotationPdfData {
  quotationNumber: string;
  issueDate: string;
  drawingReceivedDate?: string;
  entity?: string;
  clientName: string;
  companyName?: string;
  abn?: string;
  clientAddress?: string;
  clientMobile?: string;
  clientEmail?: string;
  projectScope?: string;
  projectType?: string;
  projectDescription?: string;
  projectStreetAddress?: string;
  projectSuburb?: string;
  postCode?: string;
  council?: string;
  senderDisplayName?: string;
  senderAddress?: string;
  senderMobile?: string;
  senderEmail?: string;
  senderPayId?: string;
  senderBsb?: string;
  senderAccountNumber?: string;
  senderAccountName?: string;
  signOnlineUrl?: string;
  signatoryEmail?: string;
  signatoryName?: string;
  signatoryCompany?: string;
  signatoryPosition?: string;
  exclusions?: string[];
  termsConditions?: string[];
  structuralScopeFootnote?: string;
  civilScopeFootnote?: string;
  structuralFee?: number;
  civilFee?: number;
  soilReportFee?: number;
  hourlyRate?: number;
  inspectionFee?: number;
  quoteValidityDays?: number;
  projectManagementFee?: number;
  quotedFeeTotal?: number;
  totalQuote?: number;
  scopeSections?: QuotationScopeCatalogueSection[];
  paymentSchedule?: QuotationPaymentStage[];
}

interface QuotationTemplateProps {
  data: QuotationPdfData;
}

export type QuotationPaymentStageKey = 'deposit' | 'stage2' | 'stage3' | 'final' | string;

export interface QuotationPaymentStage {
  key: string;
  stageKey: QuotationPaymentStageKey;
  componentKey: string;
  componentLabel: string;
  label: string;
  requestedPercentage: number;
  percentage: number;
  amount: number;
  reference: string;
  description?: string;
  triggerLabel?: string;
}

export const QUOTATION_EXCLUSIONS_PAGE_INDEX = 1;
export const QUOTATION_ACCEPTANCE_PAGE_INDEX = 2;
export const QUOTATION_DIGITAL_SIGNING_STAMP_ANCHOR = 'pmapp://quotation-digital-signing-stamp-anchor';

export type QuotationPdfTextFieldSpec = {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  multiline?: boolean;
};

export type QuotationPdfCheckboxFieldSpec = {
  name: string;
  x: number;
  y: number;
  size: number;
  checked?: boolean;
  label?: string;
  labelX?: number;
  labelWidth?: number;
};

const ACCEPTANCE_VERTICAL_OFFSET = QUOTATION_PDF_SPACING.acceptanceInset - QUOTATION_PDF_SPACING.pageTop;
const ACCEPTANCE_DENSE_SUMMARY_CLEARANCE = 40;
const ACCEPTANCE_HEADING_TOP = 336 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE;
const ACCEPTANCE_SUBHEADING_TOP = 358 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE;
const ACCEPTANCE_ROW_TOP = 385 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE;
const ACCEPTANCE_ROW_GAP = 21;
const ACCEPTANCE_TEXT_FIELD_HEIGHT = 15;
const ACCEPTANCE_GUIDE_LINE_OFFSET = 11;
const ACCEPTANCE_OPTION_CHECKBOX_TOP = 702 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE;
const ACCEPTANCE_OPTION_CHECKBOX_SIZE = 10;

type AcceptanceRowDefinition = {
  name: string;
  label: string;
  note?: string;
};

const ACCEPTANCE_ROW_DEFINITIONS: AcceptanceRowDefinition[] = [
  { name: 'signed', label: 'SIGNED:' },
  { name: 'signedBy', label: 'SIGNED BY:', note: '(Please Print Name)' },
  { name: 'date', label: 'Date:' },
  { name: 'client', label: 'Client:', note: '(Please Print Name)' },
  { name: 'company', label: 'Company:' },
  { name: 'abn', label: 'ABN:' },
  { name: 'address', label: 'ADDRESS:', note: '(required)' },
  { name: 'phone', label: 'PHONE:', note: '(required)' },
  { name: 'email', label: 'EMAIL:', note: '(required)' },
];

const getAcceptanceRowY = (index: number) => ACCEPTANCE_ROW_TOP + index * ACCEPTANCE_ROW_GAP;

export const QUOTATION_ACCEPTANCE_TEXT_FIELDS: QuotationPdfTextFieldSpec[] = ACCEPTANCE_ROW_DEFINITIONS.map(
  (row, index) => ({
    name: row.name,
    x: 150,
    y: getAcceptanceRowY(index),
    width: 344,
    height: ACCEPTANCE_TEXT_FIELD_HEIGHT,
  }),
);

export const QUOTATION_ACCEPTANCE_CHECKBOX_FIELDS: QuotationPdfCheckboxFieldSpec[] = [
  {
    name: 'soilReport',
    x: 36,
    y: ACCEPTANCE_OPTION_CHECKBOX_TOP - 20,
    size: ACCEPTANCE_OPTION_CHECKBOX_SIZE,
    label: 'Soil Report',
    labelX: 50,
    labelWidth: 240,
  },
  {
    name: 'sewerInformation',
    x: 320,
    y: ACCEPTANCE_OPTION_CHECKBOX_TOP - 20,
    size: ACCEPTANCE_OPTION_CHECKBOX_SIZE,
    label: 'Sewer Information (SDO) ($80+GST)',
    labelX: 334,
    labelWidth: 220,
  },
  {
    name: 'legalPointOfDischarge',
    x: 36,
    y: ACCEPTANCE_OPTION_CHECKBOX_TOP,
    size: ACCEPTANCE_OPTION_CHECKBOX_SIZE,
    label: 'Legal Point of Discharge ($300+GST)',
    labelX: 50,
    labelWidth: 240,
  },
  {
    name: 'wsudReport',
    x: 320,
    y: ACCEPTANCE_OPTION_CHECKBOX_TOP,
    size: ACCEPTANCE_OPTION_CHECKBOX_SIZE,
    label: 'WSUD Report ($600+GST)',
    labelX: 334,
    labelWidth: 220,
  },
];

const DEFAULT_TERMS_CONDITIONS = [
  'Additional site visits beyond the aforementioned allowance will be charged at the inspection fee listed for this quotation + GST.',
  'Additional work should the need arise will be charged at the hourly rate listed for this quotation + GST.',
  'This quotation is valid for 30 days from the date of quotation.',
] as const;

const styles = StyleSheet.create({
  page: {
    backgroundColor: COLORS.white,
    color: COLORS.ink,
    fontFamily: QUOTATION_PDF_FONT_FAMILY,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    paddingTop: QUOTATION_PDF_SPACING.pageTop,
    paddingRight: QUOTATION_PDF_SPACING.page,
    paddingBottom: 72,
    paddingLeft: QUOTATION_PDF_SPACING.page,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.body,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: QUOTATION_PDF_SPACING.sm,
  },
  logoWrap: {
    width: 180,
    height: 46,
    justifyContent: 'center',
  },
  equilibriumLogoWrap: {
    width: 220,
    height: 64,
    justifyContent: 'center',
  },
  headerTitleWrap: {
    alignItems: 'flex-end',
    paddingTop: 2,
  },
  title: {
    fontSize: QUOTATION_PDF_FONT_SIZES.title,
    fontWeight: 'bold',
    color: COLORS.accent,
    letterSpacing: 1,
  },
  letterPanel: {
    position: 'relative',
    width: '100%',
    marginBottom: QUOTATION_PDF_SPACING.lg,
  },
  recipientName: {
    paddingRight: 180,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    marginBottom: QUOTATION_PDF_SPACING.xxs,
  },
  recipientDate: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 170,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    textAlign: 'right',
  },
  recipientLine: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    marginBottom: QUOTATION_PDF_SPACING.xxs,
  },
  subjectLabel: {
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
    fontWeight: 'bold',
    color: COLORS.muted,
    marginTop: QUOTATION_PDF_SPACING.md,
  },
  subjectValue: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.ink,
    marginTop: QUOTATION_PDF_SPACING.xxs,
    marginBottom: QUOTATION_PDF_SPACING.sm,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.body,
  },
  salutation: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    marginBottom: QUOTATION_PDF_SPACING.md,
  },
  letterParagraph: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.paragraph,
  },
  letterParagraphPanel: {
    marginBottom: QUOTATION_PDF_SPACING.md,
  },
  band: {
    borderWidth: 1,
    borderColor: COLORS.border,
    marginBottom: 10,
  },
  bandTop: {
    backgroundColor: COLORS.topPanel,
    padding: 10,
  },
  bandProject: {
    backgroundColor: COLORS.projectPanel,
    padding: 10,
  },
  bandFees: {
    backgroundColor: COLORS.feePanel,
    padding: 10,
  },
  bandHeading: {
    fontSize: QUOTATION_PDF_FONT_SIZES.sectionHeading,
    fontWeight: 'bold',
    marginBottom: QUOTATION_PDF_SPACING.md,
    color: COLORS.ink,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
    paddingTop: 4,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#d9d9d9',
  },
  rowNoBorder: {
    borderBottomWidth: 0,
  },
  label: {
    width: '36%',
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  value: {
    width: '64%',
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    textAlign: 'right',
    color: COLORS.accent,
  },
  amountValue: {
    width: '64%',
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    textAlign: 'right',
    color: COLORS.accent,
    fontWeight: 'bold',
  },
  sectionTitle: {
    fontSize: QUOTATION_PDF_FONT_SIZES.sectionHeading,
    fontWeight: 'bold',
    marginBottom: QUOTATION_PDF_SPACING.sm,
  },
  scopeSection: {
    borderWidth: 1,
    borderColor: '#d5dbe2',
    padding: 8,
    marginBottom: 8,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: QUOTATION_PDF_SPACING.xs,
  },
  bullet: {
    width: 10,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
  },
  bulletText: {
    flex: 1,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    color: COLORS.ink,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.body,
  },
  summaryNote: {
    marginTop: QUOTATION_PDF_SPACING.sm,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    color: COLORS.muted,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.paragraph,
  },
  footer: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    right: QUOTATION_PDF_SPACING.page,
    bottom: QUOTATION_PDF_SPACING.pageBottom,
    paddingTop: QUOTATION_PDF_SPACING.sm,
    borderTopWidth: 1,
    borderTopColor: COLORS.line,
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.muted,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.body,
    textAlign: 'center',
  },
  footerLine: {
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.muted,
    textAlign: 'center',
    marginBottom: 1,
  },
  paymentPanel: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: COLORS.line,
    padding: 10,
    backgroundColor: '#f8fafc',
  },
  paymentScheduleGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  paymentScheduleCard: {
    width: '49%',
    borderWidth: 1,
    borderColor: COLORS.line,
    borderRadius: 8,
    padding: QUOTATION_PDF_SPACING.md,
    backgroundColor: COLORS.white,
    marginBottom: QUOTATION_PDF_SPACING.md,
  },
  paymentScheduleLabel: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginBottom: QUOTATION_PDF_SPACING.xs,
  },
  paymentScheduleFee: {
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
    fontWeight: 'bold',
    color: COLORS.ink,
    marginBottom: QUOTATION_PDF_SPACING.xs,
  },
  paymentStageBlock: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 6,
    marginTop: 6,
  },
  paymentStageHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 10,
  },
  paymentStageTitle: {
    flex: 1,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  paymentStageAmount: {
    fontSize: QUOTATION_PDF_FONT_SIZES.bodyLarge,
    fontWeight: 'bold',
    color: COLORS.accent,
    textAlign: 'right',
  },
  paymentStageMetaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    marginTop: 2,
  },
  paymentStageMeta: {
    flex: 1,
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.muted,
  },
  paymentStageReference: {
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.muted,
    marginTop: 1,
  },
  paymentScheduleMeta: {
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
    color: COLORS.muted,
    marginBottom: QUOTATION_PDF_SPACING.xxs,
  },
  paymentScheduleAmount: {
    fontSize: QUOTATION_PDF_FONT_SIZES.sectionHeading,
    fontWeight: 'bold',
    color: COLORS.ink,
    marginTop: QUOTATION_PDF_SPACING.xxs,
  },
  paymentHeading: {
    fontSize: QUOTATION_PDF_FONT_SIZES.sectionHeading,
    fontWeight: 'bold',
    marginBottom: QUOTATION_PDF_SPACING.sm,
    color: COLORS.ink,
  },
  paymentSubheading: {
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    color: COLORS.muted,
    marginBottom: QUOTATION_PDF_SPACING.xxs,
  },
  paymentText: {
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    marginBottom: QUOTATION_PDF_SPACING.xs,
    color: COLORS.ink,
  },
  scopeGroup: {
    marginBottom: QUOTATION_PDF_SPACING.lg,
    paddingTop: QUOTATION_PDF_SPACING.sm,
    paddingBottom: QUOTATION_PDF_SPACING.sm,
  },
  scopeGroupHeader: {
    fontSize: QUOTATION_PDF_FONT_SIZES.bodyLarge,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginBottom: QUOTATION_PDF_SPACING.xs,
  },
  scopeGroupHint: {
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
    color: COLORS.muted,
    marginTop: QUOTATION_PDF_SPACING.xs,
  },
  serviceFeePanel: {
    alignSelf: 'stretch',
    marginTop: QUOTATION_PDF_SPACING.md,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'baseline',
  },
  scopeFootnoteBlock: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#d5dbe2',
    borderRadius: 6,
    padding: 7,
    backgroundColor: '#fbfdff',
  },
  scopeFootnoteLabel: {
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginBottom: QUOTATION_PDF_SPACING.xxs,
  },
  scopeFootnoteText: {
    fontSize: QUOTATION_PDF_FONT_SIZES.caption,
    color: COLORS.ink,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.body,
  },
  scopeFallbackPanel: {
    paddingTop: QUOTATION_PDF_SPACING.xs,
  },
  serviceFeeLabel: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  serviceFeeValue: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginLeft: QUOTATION_PDF_SPACING.sm,
  },
  exclusionsPage: {
    backgroundColor: COLORS.white,
    color: COLORS.ink,
    fontFamily: QUOTATION_PDF_FONT_FAMILY,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    paddingTop: QUOTATION_PDF_SPACING.pageTop,
    paddingRight: QUOTATION_PDF_SPACING.page,
    paddingBottom: QUOTATION_PDF_SPACING.pageBottom,
    paddingLeft: QUOTATION_PDF_SPACING.page,
  },
  exclusionsHeading: {
    fontSize: QUOTATION_PDF_FONT_SIZES.pageHeading,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginBottom: QUOTATION_PDF_SPACING.lg,
  },
  exclusionsIntro: {
    fontSize: QUOTATION_PDF_FONT_SIZES.bodyLarge,
    color: COLORS.ink,
    marginBottom: QUOTATION_PDF_SPACING.xxl,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.paragraph,
  },
  exclusionsList: {
    marginBottom: QUOTATION_PDF_SPACING.xl,
  },
  exclusionBulletRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: QUOTATION_PDF_SPACING.xs,
    paddingRight: QUOTATION_PDF_SPACING.md,
  },
  exclusionBullet: {
    width: 12,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
  },
  exclusionBulletText: {
    flex: 1,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.tight,
    color: COLORS.ink,
  },
  exclusionsEmpty: {
    fontSize: QUOTATION_PDF_FONT_SIZES.bodyLarge,
    color: COLORS.muted,
    marginBottom: QUOTATION_PDF_SPACING.xl,
  },
  exclusionsClosingPanel: {
    marginTop: QUOTATION_PDF_SPACING.md,
    marginBottom: QUOTATION_PDF_SPACING.xl,
  },
  exclusionsClosingLine: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    marginBottom: QUOTATION_PDF_SPACING.xs,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.paragraph,
  },
  digitalSigningStampAnchor: {
    width: 96,
    height: 26,
    marginTop: QUOTATION_PDF_SPACING.xs,
    marginBottom: QUOTATION_PDF_SPACING.sm,
    marginLeft: QUOTATION_PDF_SPACING.md,
    color: COLORS.white,
    textDecoration: 'none',
  },
  acceptancePage: {
    backgroundColor: COLORS.white,
    color: COLORS.ink,
    fontFamily: QUOTATION_PDF_FONT_FAMILY,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    padding: 0,
    position: 'relative',
  },
  acceptanceSummaryPanel: {
    position: 'absolute',
    top: QUOTATION_PDF_SPACING.pageTop + ACCEPTANCE_VERTICAL_OFFSET,
    left: QUOTATION_PDF_SPACING.page,
    right: QUOTATION_PDF_SPACING.page,
    borderWidth: 1,
    borderColor: COLORS.line,
    paddingTop: QUOTATION_PDF_SPACING.md,
    paddingRight: QUOTATION_PDF_SPACING.md,
    paddingBottom: QUOTATION_PDF_SPACING.md,
    paddingLeft: QUOTATION_PDF_SPACING.md,
    backgroundColor: COLORS.white,
  },
  acceptanceSummaryHeading: {
    fontSize: QUOTATION_PDF_FONT_SIZES.sectionHeading,
    fontWeight: 'bold',
    color: COLORS.accent,
    marginBottom: QUOTATION_PDF_SPACING.sm,
  },
  acceptanceSummaryHeadingCompact: {
    fontSize: 12.8,
    marginBottom: 3,
  },
  acceptanceSummaryRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: QUOTATION_PDF_SPACING.md,
    paddingTop: 2,
    paddingBottom: 2,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.lineSoft,
  },
  acceptanceSummaryRowCompact: {
    gap: QUOTATION_PDF_SPACING.sm,
    paddingTop: 1,
    paddingBottom: 1,
  },
  acceptanceSummaryLabel: {
    width: '36%',
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  acceptanceSummaryLabelCompact: {
    width: '33%',
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
  },
  acceptanceSummaryValue: {
    width: '64%',
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    textAlign: 'left',
  },
  acceptanceSummaryValueCompact: {
    width: '67%',
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
  },
  acceptanceSummaryPaymentValue: {
    width: '64%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: QUOTATION_PDF_SPACING.sm,
  },
  acceptanceSummaryPaymentValueCompact: {
    width: '67%',
  },
  acceptanceSummaryPaymentAmount: {
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    minWidth: 70,
  },
  acceptanceSummaryPaymentAmountCompact: {
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
    minWidth: 58,
  },
  acceptanceSummaryPaymentTrigger: {
    flex: 1,
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.muted,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.tight,
    textAlign: 'right',
  },
  acceptanceSummaryNote: {
    fontSize: QUOTATION_PDF_FONT_SIZES.small,
    color: COLORS.muted,
    marginTop: QUOTATION_PDF_SPACING.sm,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.tight,
  },
  acceptanceSummaryNoteCompact: {
    fontSize: 6.8,
    marginTop: 3,
  },
  acceptanceHeading: {
    position: 'absolute',
    top: ACCEPTANCE_HEADING_TOP,
    left: QUOTATION_PDF_SPACING.page,
    fontSize: QUOTATION_PDF_FONT_SIZES.acceptanceHeading,
    fontWeight: 'bold',
    color: COLORS.accent,
  },
  acceptanceSubheading: {
    position: 'absolute',
    top: ACCEPTANCE_SUBHEADING_TOP,
    left: QUOTATION_PDF_SPACING.page,
    width: 547,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    color: COLORS.muted,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.paragraph,
  },
  acceptanceLabel: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    width: 92,
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  acceptanceNote: {
    position: 'absolute',
    width: 120,
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.ink,
  },
  acceptanceLine: {
    position: 'absolute',
    left: 150,
    width: 344,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.acceptanceGuide,
    borderBottomStyle: 'dotted',
  },
  acceptanceCheckLabel: {
    position: 'absolute',
    fontSize: QUOTATION_PDF_FONT_SIZES.body,
    color: COLORS.ink,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.tight,
  },
  acceptanceOnlineLabel: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    top: 588 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 110,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  acceptanceOnlineButton: {
    position: 'absolute',
    left: 150,
    top: 580 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 126,
    paddingTop: 5,
    paddingRight: 12,
    paddingBottom: 5,
    paddingLeft: 12,
    borderRadius: 999,
    backgroundColor: COLORS.accent,
    color: COLORS.white,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    fontWeight: 'bold',
    textAlign: 'center',
    textDecoration: 'none',
  },
  acceptanceOnlineFallback: {
    position: 'absolute',
    left: 290,
    top: 582 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 285,
    fontSize: QUOTATION_PDF_FONT_SIZES.micro,
    color: COLORS.muted,
    lineHeight: QUOTATION_PDF_LINE_HEIGHTS.tight,
  },
  acceptanceOnlineFallbackUrl: {
    color: COLORS.accent,
    textDecoration: 'underline',
  },
  acceptanceCheckboxOutline: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: COLORS.ink,
  },
  depositInstruction: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    top: 606 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 547,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  depositDetails: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    top: 646 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 547,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  depositPayId: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    top: 630 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 547,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
  depositDetailsSecondary: {
    position: 'absolute',
    left: QUOTATION_PDF_SPACING.page,
    top: 662 + ACCEPTANCE_VERTICAL_OFFSET + ACCEPTANCE_DENSE_SUMMARY_CLEARANCE,
    width: 547,
    fontSize: QUOTATION_PDF_FONT_SIZES.bodySmall,
    fontWeight: 'bold',
    color: COLORS.ink,
  },
});

function formatDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatCurrency(value?: number): string {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatPercentage(value?: number): string {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return `${Math.round(amount * 100)}%`;
}

function formatLocation(value?: string): string {
  return value && value.trim() ? value : '-';
}

function toTrimmedString(value?: string): string {
  return String(value || '').trim();
}

function normalizeDescriptorPart(value: string): string {
  return value.toLowerCase().replace(/\s+/g, ' ');
}

function buildProjectDescriptorParts(data: QuotationPdfData): string[] {
  const descriptorParts: string[] = [];
  const seenParts = new Set<string>();

  [data.projectScope, data.projectType].forEach((value) => {
    const part = toTrimmedString(value);
    if (!part) {
      return;
    }

    const normalizedPart = normalizeDescriptorPart(part);
    if (seenParts.has(normalizedPart)) {
      return;
    }

    seenParts.add(normalizedPart);
    descriptorParts.push(part);
  });

  return descriptorParts;
}

function buildProjectServiceDescriptor(data: QuotationPdfData): string {
  return buildProjectDescriptorParts(data).join(' ');
}

function buildGreetingLabel(data: QuotationPdfData): string {
  return toTrimmedString(data.clientName)
    || toTrimmedString(data.companyName)
    || 'Sir/Madam';
}

function isEquilibriumEntity(entity?: string): boolean {
  return toTrimmedString(entity).toLowerCase() === 'equilibrium engineering pty ltd';
}

export function buildSignatoryProfile(data: QuotationPdfData) {
  const entity = toTrimmedString(data.entity) || 'Linfinity Engineering Pty Ltd';
  const company = resolveQuotationSignatoryCompany({
    name: data.signatoryName,
    email: data.signatoryEmail,
    company: data.signatoryCompany,
    entity,
  });
  if (isEquilibriumEntity(entity)) {
    return {
      name: toTrimmedString(data.signatoryName) || 'Wilson Du',
      company,
      position: toTrimmedString(data.signatoryPosition) || 'Director',
    };
  }

  return {
    name: toTrimmedString(data.signatoryName) || 'Yuan Lin',
    company,
    position: toTrimmedString(data.signatoryPosition) || 'Director',
  };
}

function formatPaymentStageLabel(stage: QuotationPaymentStage): string {
  const label = stage.label?.trim();
  if (label) {
    return label;
  }

  switch (stage.stageKey) {
    case 'deposit':
      return 'Deposit';
    case 'stage2':
      return 'Stage 2 Payment';
    case 'stage3':
      return 'Stage 3 Payment';
    case 'final':
      return 'Final Payment';
    default:
      return 'Payment Stage';
  }
}

function formatPaymentStageTrigger(stage: QuotationPaymentStage): string {
  return toTrimmedString(stage.triggerLabel) || toTrimmedString(stage.reference);
}

function normalizePaymentStageKey(value?: string): string {
  return (value || '').trim().toLowerCase();
}

function normalizePaymentStages(stages: QuotationPaymentStage[]) {
  const groups = new Map<string, {
    componentKey: string;
    componentLabel: string;
    stages: QuotationPaymentStage[];
  }>();

  stages
    .filter((stage) => stage && Number(stage.amount) > 0)
    .forEach((stage) => {
      const componentKey = normalizePaymentStageKey(stage.componentKey) || 'general';
      const componentLabel = stage.componentLabel?.trim() || componentKey.replace(/(^|-)([a-z])/g, (_, __, char: string) => char.toUpperCase());
      if (!groups.has(componentKey)) {
        groups.set(componentKey, {
          componentKey,
          componentLabel,
          stages: [],
        });
      }
      groups.get(componentKey)!.stages.push(stage);
    });

  const componentOrder = (key: string) => {
    if (key === 'structural') return 0;
    if (key === 'civil') return 1;
    return 2;
  };

  const stageOrder = (key?: string) => {
    switch (normalizePaymentStageKey(key)) {
      case 'deposit':
        return 0;
      case 'stage2':
        return 1;
      case 'stage3':
        return 2;
      case 'final':
        return 3;
      default:
        return 4;
    }
  };

  return Array.from(groups.values())
    .sort((left, right) => {
      const rankDiff = componentOrder(left.componentKey) - componentOrder(right.componentKey);
      if (rankDiff !== 0) return rankDiff;
      return left.componentLabel.localeCompare(right.componentLabel);
    })
    .map((group) => ({
      ...group,
      stages: [...group.stages].sort((left, right) => {
        const rankDiff = stageOrder(left.stageKey) - stageOrder(right.stageKey);
        if (rankDiff !== 0) return rankDiff;
        return left.label.localeCompare(right.label);
      }),
    }))
    .filter((group) => group.stages.length > 0);
}

function buildProjectLocation(data: QuotationPdfData): string {
  return [data.projectStreetAddress, data.projectSuburb, data.postCode].filter(Boolean).join(' ');
}

export function groupScopeSections(sections: QuotationScopeCatalogueSection[]) {
  return {
    structural: sections.filter((section) => section.tint === 'structural'),
    civil: sections.filter((section) => section.tint === 'civil'),
    general: sections.filter((section) => section.tint === 'general'),
  };
}

function dedupeBullets(sections: QuotationScopeCatalogueSection[]): string[] {
  const seen = new Set<string>();
  const bullets: string[] = [];

  sections.forEach((section) => {
    section.bullets.forEach((bullet) => {
      const normalized = bullet.trim();
      const key = normalized.toLowerCase();
      if (!normalized || seen.has(key)) {
        return;
      }
      seen.add(key);
      bullets.push(normalized);
    });
  });

  return bullets;
}

export function buildProjectHeadline(data: QuotationPdfData): string {
  const site = buildProjectLocation(data);
  const descriptor = buildProjectDescriptorParts(data).join(' - ');

  if (descriptor && site) {
    return `Quotation for ${descriptor} at: ${site}`;
  }

  if (descriptor) {
    return `Quotation for ${descriptor}`;
  }

  if (site) {
    return `Quotation for: ${site}`;
  }

  return 'Quotation for the above project';
}

function buildProjectName(data: QuotationPdfData): string {
  const description = String(data.projectDescription || '').trim();
  if (description) {
    return description;
  }

  const type = String(data.projectType || '').trim();
  const site = buildProjectLocation(data);
  if (type && site) {
    return `${type} at ${site}`;
  }

  return type || site || formatLocation(data.projectScope);
}

function buildSignOnlineUrl(value?: string): string {
  return String(value || '').trim();
}

function QuotationFooter({ paymentProfile, quotationNumber }: {
  paymentProfile: {
    senderDisplayName: string;
    senderAddress: string;
    senderMobile: string;
    senderEmail: string;
  };
  quotationNumber?: string;
}) {
  return (
    <View fixed wrap={false} style={styles.footer}>
      <Text style={styles.footerLine}>{paymentProfile.senderDisplayName}</Text>
      <Text style={styles.footerLine}>{paymentProfile.senderAddress}</Text>
      <Text style={styles.footerLine}>{`M: ${paymentProfile.senderMobile}    E: ${paymentProfile.senderEmail}`}</Text>
      <Text style={styles.footerLine}>Quotation Reference: {quotationNumber || 'DRAFT'}</Text>
    </View>
  );
}

export function buildIntroParagraph(data: QuotationPdfData): string {
  const serviceDescriptor = buildProjectServiceDescriptor(data);
  const drawingReceivedDate = toTrimmedString(data.drawingReceivedDate || data.issueDate);
  const receivedDate = drawingReceivedDate ? formatDate(drawingReceivedDate) : '';

  if (serviceDescriptor && receivedDate) {
    return `Thank you for the opportunity to submit the following fee proposal for the ${serviceDescriptor} services for the above project. This quotation is based on the drawings received on ${receivedDate}.`;
  }

  if (serviceDescriptor) {
    return `Thank you for the opportunity to submit the following fee proposal for the ${serviceDescriptor} services for the above project.`;
  }

  if (receivedDate) {
    return `Thank you for the opportunity to submit the following fee proposal for the above project. This quotation is based on the drawings received on ${receivedDate}.`;
  }

  return 'Thank you for the opportunity to submit the following fee proposal for the above project.';
}

function buildProjectSummaryParagraph(data: QuotationPdfData): string {
  const description = toTrimmedString(data.projectDescription);
  if (!description) {
    return 'The scope of our services is divided into the following parts:';
  }

  return `The project consists of ${description}, and the scope of our services is divided into the following parts:`;
}

function normalizeTermsConditions(termsConditions?: string[]): string[] {
  const normalized = (termsConditions || [])
    .map((item) => String(item || '').trim())
    .filter(Boolean);

  return normalized.length > 0
    ? normalized
    : [...DEFAULT_TERMS_CONDITIONS];
}

function formatCurrencyWithCents(value?: number): string {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatHourlyRate(value?: number): string {
  const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function resolveTermsConditions(data: QuotationPdfData): string[] {
  const hourlyRate = typeof data.hourlyRate === 'number' && Number.isFinite(data.hourlyRate) ? data.hourlyRate : DEFAULT_QUOTATION_HOURLY_RATE;
  const inspectionFee = typeof data.inspectionFee === 'number' && Number.isFinite(data.inspectionFee) ? data.inspectionFee : DEFAULT_QUOTATION_INSPECTION_FEE;

  return normalizeTermsConditions(data.termsConditions).map((item) => {
    const normalized = item.toLowerCase();
    if (normalized.includes('site visits beyond the aforementioned allowance')) {
      return `Additional site visits beyond the aforementioned allowance will be charged at ${formatCurrencyWithCents(inspectionFee)} + GST.`;
    }
    if (normalized.includes('this quotation is valid for')) {
      const days = Number.isInteger(data.quoteValidityDays) && data.quoteValidityDays! > 0 ? data.quoteValidityDays! : 30;
      return `This quotation is valid for ${days} ${days === 1 ? 'day' : 'days'} from the date of quotation.`;
    }
    if (normalized.includes('hourly rate')) {
      return `Additional work should the need arise will be charged at an hourly rate of ${formatHourlyRate(hourlyRate)}/h + GST.`;
    }
    return item;
  });
}

function normalizeScopeFootnoteText(value?: string): string {
  return String(value || '')
    .replace(/\r\n/g, '\n')
    .trim();
}

type RenderedServiceSection = {
  key: string;
  title: string;
  tint: 'structural' | 'civil' | 'general';
  bullets: string[];
  fee?: number;
  feeValue?: string;
  feeLabel?: string;
  note?: string;
  footnote?: string;
};

export function buildRenderedServiceSections(
  data: QuotationPdfData,
  groups: ReturnType<typeof groupScopeSections>
): RenderedServiceSection[] {
  const sections: RenderedServiceSection[] = [];
  const structuralFee = typeof data.structuralFee === 'number' ? data.structuralFee : 0;
  const civilFee = typeof data.civilFee === 'number' ? data.civilFee : 0;
  const consultantFee = (typeof data.soilReportFee === 'number' ? data.soilReportFee : 0)
    + (typeof data.projectManagementFee === 'number' ? data.projectManagementFee : 0);

  const structuralBullets = dedupeBullets(groups.structural);
  const structuralFootnote = normalizeScopeFootnoteText(data.structuralScopeFootnote);
  if (structuralFee > 0 || structuralBullets.length > 0 || structuralFootnote) {
    sections.push({
      key: 'structural',
      title: 'Structural Design and Documentation',
      tint: 'structural',
      bullets: structuralBullets.length > 0
        ? structuralBullets
        : ['Structural engineering design and documentation services as scoped for this project.'],
      fee: structuralFee,
      feeLabel: 'Total Fee for Structural Engineering Design and Documentation',
      footnote: structuralFootnote || undefined,
    });
  }

  const civilBullets = dedupeBullets(groups.civil);
  const civilFootnote = normalizeScopeFootnoteText(data.civilScopeFootnote);
  if (civilFee > 0 || civilBullets.length > 0 || civilFootnote) {
    sections.push({
      key: 'civil',
      title: 'Civil Design and Documentation',
      tint: 'civil',
      bullets: civilBullets.length > 0
        ? civilBullets
        : ['Civil engineering design and documentation services as scoped for this project.'],
      fee: civilFee,
      feeLabel: 'Total Fee for Civil Engineering Design and Documentation',
      footnote: civilFootnote || undefined,
    });
  }

  const generalBullets = dedupeBullets(groups.general);
  if (generalBullets.length > 0) {
    sections.push({
      key: 'general',
      title: 'Additional Scope / Other Services',
      tint: 'general',
      bullets: generalBullets,
      feeLabel: 'Total Fee for Additional Scope / Other Services',
      feeValue: 'Included in Structural Fee',
    });
  }

  if (consultantFee > 0) {
    sections.push({
      key: 'consultant',
      title: 'Additional External Consultant/Authority Fees (as provisional sum)',
      tint: 'general',
      bullets: [
        typeof data.soilReportFee === 'number' && data.soilReportFee > 0
          ? `Soil Report for proposed development (${formatCurrency(data.soilReportFee)} + GST P.S.)`
          : '',
      ].filter(Boolean),
      fee: consultantFee,
      feeLabel: 'Total Fee for Additional Consultant Report (as provisional sum)',
      note: 'We can organise the following from relevant authority / consultants on behalf of the client.',
    });
  }

  return sections;
}

const ACCEPTANCE_ROWS = ACCEPTANCE_ROW_DEFINITIONS.map((row, index) => ({
  label: row.label,
  y: getAcceptanceRowY(index),
  note: row.note,
}));

const QuotationTemplate: React.FC<QuotationTemplateProps> = ({ data }) => {
  const scopeSections = data.scopeSections && data.scopeSections.length > 0
    ? data.scopeSections
    : getQuotationScopeCatalogueSelection({
        projectScope: data.projectScope,
        projectType: data.projectType,
      });
  const scopeGroups = groupScopeSections(scopeSections);
  const exclusions = Array.isArray(data.exclusions)
    ? dedupeQuotationExclusions(data.exclusions)
    : [...DEFAULT_QUOTATION_EXCLUSION_OPTIONS];
  const termsConditions = resolveTermsConditions(data);

  const structuralFee = typeof data.structuralFee === 'number' ? data.structuralFee : 0;
  const civilFee = typeof data.civilFee === 'number' ? data.civilFee : 0;
  const soilReportFee = typeof data.soilReportFee === 'number' ? data.soilReportFee : 0;
  const projectManagementFee = typeof data.projectManagementFee === 'number' ? data.projectManagementFee : 0;
  const quotedFeeTotal = typeof data.quotedFeeTotal === 'number'
    ? data.quotedFeeTotal
    : structuralFee + civilFee + projectManagementFee;
  const depositReference = `${data.quotationNumber || 'DRAFT'}-1`;
  const paymentProfile = {
    entity: data.entity || 'Linfinity Engineering Pty Ltd',
    senderDisplayName: data.senderDisplayName || 'Linfinity Engineering Pty Ltd (ABN 64 427 178 431)',
    senderAddress: data.senderAddress || 'Suite 261, Tenancy 111, Camberwell Place, 793 Burke Road, Camberwell, VIC 3124',
    senderMobile: data.senderMobile || '0468 886 926',
    senderEmail: data.senderEmail || 'yl@linfinityeng.com',
    senderPayId: data.senderPayId || '',
    senderBsb: data.senderBsb || '013-479',
    senderAccountNumber: data.senderAccountNumber || '4177-69018',
    senderAccountName: data.senderAccountName || 'Linfinity Engineering Pty Ltd',
  };
  const projectHeadline = buildProjectHeadline(data);
  const projectName = buildProjectName(data);
  const greetingLabel = buildGreetingLabel(data);
  const signatory = buildSignatoryProfile(data);
  const isEquilibrium = isEquilibriumEntity(paymentProfile.entity);
  const recipientNameLine = data.clientName || '';
  const recipientLines = [
    data.companyName || '',
    data.clientAddress ? `Address: ${data.clientAddress}` : '',
  ].filter(Boolean);
  const serviceSections = buildRenderedServiceSections(data, scopeGroups);

  const paymentSchedule = Array.isArray(data.paymentSchedule)
    ? data.paymentSchedule.filter((stage) => stage && Number.isFinite(Number(stage.amount)))
    : [];
  const paymentScheduleGroups = normalizePaymentStages(paymentSchedule);
  const paymentSummaryStages = paymentScheduleGroups.flatMap((group) =>
    group.stages.map((stage) => ({
      group,
      stage,
    }))
  );
  const feeSummaryLines = [
    structuralFee > 0 ? { label: 'Structural Engineering Design', amount: structuralFee } : null,
    civilFee > 0 ? { label: 'Civil Engineering Design', amount: civilFee } : null,
    soilReportFee > 0
      ? { label: 'Additional Consultant Report (P.S.)', amount: soilReportFee, note: 'Estimated soil report fee' }
      : null,
    projectManagementFee > 0 ? { label: 'Project Management Fee', amount: projectManagementFee } : null,
  ].filter((row): row is { label: string; amount: number; note?: string } => Boolean(row));
  const useCompactAcceptanceSummary = feeSummaryLines.length + paymentSummaryStages.length >= 10;
  const summaryRowStyle = useCompactAcceptanceSummary
    ? [styles.acceptanceSummaryRow, styles.acceptanceSummaryRowCompact]
    : styles.acceptanceSummaryRow;
  const summaryLabelStyle = useCompactAcceptanceSummary
    ? [styles.acceptanceSummaryLabel, styles.acceptanceSummaryLabelCompact]
    : styles.acceptanceSummaryLabel;
  const summaryValueStyle = useCompactAcceptanceSummary
    ? [styles.acceptanceSummaryValue, styles.acceptanceSummaryValueCompact]
    : styles.acceptanceSummaryValue;
  const paymentSummaryValueStyle = useCompactAcceptanceSummary
    ? [styles.acceptanceSummaryPaymentValue, styles.acceptanceSummaryPaymentValueCompact]
    : styles.acceptanceSummaryPaymentValue;
  const paymentSummaryAmountStyle = useCompactAcceptanceSummary
    ? [styles.acceptanceSummaryPaymentAmount, styles.acceptanceSummaryPaymentAmountCompact]
    : styles.acceptanceSummaryPaymentAmount;
  const initialPaymentStage = paymentSummaryStages[0]?.stage ?? null;
  const signOnlineUrl = buildSignOnlineUrl(data.signOnlineUrl);

  return (
    <Document title={`Quotation ${data.quotationNumber}`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View style={isEquilibrium ? styles.equilibriumLogoWrap : styles.logoWrap}>
            <Image
              src={isEquilibrium ? EquilibriumLogoPNG : LogoPNG}
              style={isEquilibrium ? { width: 220, height: 72, objectFit: 'contain' } : { width: 170, objectFit: 'contain' }}
            />
          </View>
          <View style={styles.headerTitleWrap}>
            <Text style={styles.title}>QUOTATION</Text>
          </View>
        </View>

        <View style={styles.letterPanel}>
          <Text style={styles.recipientName}>{recipientNameLine}</Text>
          <Text style={styles.recipientDate}>{formatDate(data.issueDate)}</Text>
          {recipientLines.map((line) => (
            <Text key={line} style={styles.recipientLine}>{line}</Text>
          ))}
          <Text style={styles.subjectLabel}>Subject:</Text>
          <Text style={styles.subjectValue}>{projectHeadline}</Text>
          <Text style={styles.salutation}>Dear {greetingLabel},</Text>
          <View style={styles.letterParagraphPanel}>
            <Text style={styles.letterParagraph}>{buildIntroParagraph(data)}</Text>
          </View>
          <View style={styles.letterParagraphPanel}>
            <Text style={styles.letterParagraph}>{buildProjectSummaryParagraph(data)}</Text>
          </View>
        </View>

        {serviceSections.length > 0 ? serviceSections.map((section, index) => (
          <View
            key={section.key}
            style={styles.scopeGroup}
          >
            <Text style={styles.scopeGroupHeader}>{`${index + 1}. ${section.title} :`}</Text>
            {section.note ? (
              <Text style={styles.summaryNote}>{section.note}</Text>
            ) : null}
            {section.bullets.map((bullet) => (
              <View key={`${section.key}-${bullet}`} style={styles.bulletRow}>
                <Text style={styles.bullet}>•</Text>
                <Text style={styles.bulletText}>{bullet}</Text>
              </View>
            ))}
            {section.feeLabel && (typeof section.fee === 'number' || section.feeValue) ? (
              <View style={styles.serviceFeePanel}>
                <Text style={styles.serviceFeeLabel}>{section.feeLabel}</Text>
                <Text style={styles.serviceFeeValue}>
                  {typeof section.fee === 'number' ? `${formatCurrency(section.fee)} + GST` : section.feeValue}
                </Text>
              </View>
            ) : null}
            {section.footnote ? (
              <View style={styles.scopeFootnoteBlock}>
                <Text style={styles.scopeFootnoteLabel}>Special Footnote</Text>
                <Text style={styles.scopeFootnoteText}>{section.footnote}</Text>
              </View>
            ) : null}
          </View>
        )) : (
          <View style={styles.scopeFallbackPanel}>
            <Text style={styles.bulletText}>
              Detailed scope items will be confirmed based on the selected project scope and project type.
            </Text>
          </View>
        )}

        <Text style={styles.summaryNote}>
          This quotation is based on the information currently provided. Any change in architectural, structural, civil,
          or certification scope may require a revised quotation.
        </Text>

        <QuotationFooter paymentProfile={paymentProfile} quotationNumber={data.quotationNumber} />
      </Page>

      <Page size="A4" style={styles.exclusionsPage}>
        <Text style={styles.exclusionsHeading}>Exclusions</Text>
        <Text style={styles.exclusionsIntro}>
          Unless specifically stated, the above fee does not include the following items:
        </Text>

        {exclusions.length > 0 ? (
          <View style={styles.exclusionsList}>
            {exclusions.map((label) => (
              <View key={label} style={styles.exclusionBulletRow}>
                <Text style={styles.exclusionBullet}>•</Text>
                <Text style={styles.exclusionBulletText}>{label}</Text>
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.exclusionsEmpty}>No exclusions are listed for this quotation.</Text>
        )}

        <View style={styles.exclusionsClosingPanel}>
          {termsConditions.map((item) => (
            <Text key={item} style={styles.exclusionsClosingLine}>• {item}</Text>
          ))}
          <Text style={[styles.exclusionsClosingLine, { marginTop: QUOTATION_PDF_SPACING.xxl }]}>
            Should you have further queries please do not hesitate to contact us.
          </Text>
          <Text style={styles.exclusionsClosingLine}>Sincerely,</Text>
          <Link src={QUOTATION_DIGITAL_SIGNING_STAMP_ANCHOR} style={styles.digitalSigningStampAnchor}> </Link>
          <Text style={styles.exclusionsClosingLine}>{signatory.name}</Text>
          <Text style={styles.exclusionsClosingLine}>{signatory.company}</Text>
          <Text style={styles.exclusionsClosingLine}>{signatory.position}</Text>
        </View>

        <QuotationFooter paymentProfile={paymentProfile} quotationNumber={data.quotationNumber} />
      </Page>

      <Page size="A4" style={styles.acceptancePage}>
        <View style={styles.acceptanceSummaryPanel}>
          <Text style={useCompactAcceptanceSummary ? [styles.acceptanceSummaryHeading, styles.acceptanceSummaryHeadingCompact] : styles.acceptanceSummaryHeading}>
            Quotation Summary
          </Text>
          <View style={summaryRowStyle}>
            <Text style={summaryLabelStyle}>Quotation No.</Text>
            <Text style={summaryValueStyle}>{data.quotationNumber || 'DRAFT'}</Text>
          </View>
          <View style={summaryRowStyle}>
            <Text style={summaryLabelStyle}>Name of Project</Text>
            <Text style={summaryValueStyle}>{projectName}</Text>
          </View>
          <View style={summaryRowStyle}>
            <Text style={summaryLabelStyle}>Project Scope</Text>
            <Text style={summaryValueStyle}>{formatLocation(data.projectScope)}</Text>
          </View>
          <View style={summaryRowStyle}>
            <Text style={summaryLabelStyle}>Project Type</Text>
            <Text style={summaryValueStyle}>{formatLocation(data.projectType)}</Text>
          </View>
          <View style={summaryRowStyle}>
            <Text style={summaryLabelStyle}>Site</Text>
            <Text style={summaryValueStyle}>{buildProjectLocation(data)}</Text>
          </View>
          <View style={summaryRowStyle}>
            <Text style={summaryLabelStyle}>Fee Quoted</Text>
            <Text style={summaryValueStyle}>{`${formatCurrency(quotedFeeTotal)} (+ GST)`}</Text>
          </View>
          {feeSummaryLines.map((row) => (
            <View key={row.label} style={summaryRowStyle}>
              <Text style={summaryLabelStyle}>{row.label}</Text>
              {row.note ? (
                <View style={paymentSummaryValueStyle}>
                  <Text style={paymentSummaryAmountStyle}>{`${formatCurrency(row.amount)} + GST`}</Text>
                  <Text style={styles.acceptanceSummaryPaymentTrigger}>{row.note}</Text>
                </View>
              ) : (
                <Text style={summaryValueStyle}>{`${formatCurrency(row.amount)} + GST`}</Text>
              )}
            </View>
          ))}
          {paymentSummaryStages.map(({ group, stage }) => {
            const trigger = formatPaymentStageTrigger(stage);
            return (
              <View key={stage.key} style={summaryRowStyle}>
                <Text style={summaryLabelStyle}>{`${group.componentLabel} ${formatPaymentStageLabel(stage)}`}</Text>
                <View style={paymentSummaryValueStyle}>
                  <Text style={paymentSummaryAmountStyle}>{`${formatCurrency(stage.amount)} + GST`}</Text>
                  {trigger ? (
                    <Text style={styles.acceptanceSummaryPaymentTrigger}>{trigger}</Text>
                  ) : null}
                </View>
              </View>
            );
          })}
          <Text style={useCompactAcceptanceSummary ? [styles.acceptanceSummaryNote, styles.acceptanceSummaryNoteCompact] : styles.acceptanceSummaryNote}>
            All payment claims are made under the Building and Construction Industry Security of Payment Act 2002 (Vic).
          </Text>
        </View>

        <Text style={styles.acceptanceHeading}>Authority to Commence Work</Text>
        <Text style={styles.acceptanceSubheading}>
          Complete the fields below in the exported PDF and tick any additional requested reports before returning the signed quotation.
        </Text>

        {ACCEPTANCE_ROWS.map((row) => (
          <React.Fragment key={row.label}>
            <Text style={{ ...styles.acceptanceLabel, top: row.y }}>{row.label}</Text>
            <View style={{ ...styles.acceptanceLine, top: row.y + ACCEPTANCE_GUIDE_LINE_OFFSET }} />
            {row.note ? (
              <Text style={{ ...styles.acceptanceNote, top: row.y + 1, left: 500 }}>{row.note}</Text>
            ) : null}
          </React.Fragment>
        ))}

        {signOnlineUrl ? (
          <>
            <Text style={styles.acceptanceOnlineLabel}>Sign online</Text>
            <Link src={signOnlineUrl} style={styles.acceptanceOnlineButton}>
              Sign online
            </Link>
            <Text style={styles.acceptanceOnlineFallback}>Use the secure button above to complete this quotation online.</Text>
          </>
        ) : null}

        <Text style={styles.depositInstruction}>
          {initialPaymentStage
            ? `Upon accepting this quotation, initial payment of ${formatCurrency(initialPaymentStage.amount)} + GST is to be paid by Bank Transfer / PayID to:`
            : 'Upon accepting this quotation, initial payment is to be paid by Bank Transfer / PayID to:'}
        </Text>
        {paymentProfile.senderPayId ? (
          <Text style={styles.depositPayId}>
            {`PayID: ${paymentProfile.senderPayId}`}
          </Text>
        ) : null}
        <Text style={styles.depositDetails}>
          {`BSB: ${paymentProfile.senderBsb}    Account No: ${paymentProfile.senderAccountNumber}`}
        </Text>
        <Text style={styles.depositDetailsSecondary}>
          {`Account Name: ${paymentProfile.senderAccountName}    Ref: ${depositReference}`}
        </Text>

        {QUOTATION_ACCEPTANCE_CHECKBOX_FIELDS.map((field) => (
          <React.Fragment key={field.name}>
            <View
              style={{
                ...styles.acceptanceCheckboxOutline,
                left: field.x,
                top: field.y,
                width: field.size,
                height: field.size,
              }}
            />
            <Text
              style={{
                ...styles.acceptanceCheckLabel,
                left: field.labelX ?? field.x + 18,
                top: field.y - 1,
                width: field.labelWidth ?? 140,
              }}
            >
              {field.label || ''}
            </Text>
          </React.Fragment>
        ))}

        <QuotationFooter paymentProfile={paymentProfile} quotationNumber={data.quotationNumber} />
      </Page>
    </Document>
  );
};

export default QuotationTemplate;
